  import { format, transpose, intervalToDuration } from 'date-fns';
  import { TZDate } from '@date-fns/tz';
  import Bugsnag from '@bugsnag/js';
  import { i18n } from '@/i18n';
  import { dateFnsLocale } from '@/i18n/dateFnsLocale';

// date-fns format() with the active vue-i18n locale applied — use this for any
// pattern with localized tokens (month/weekday names, the p/P time presets).
// Machine formats (yyyy-MM-dd API params, keys) keep the raw format().
export const formatLocalized = (date, formatString, options = {}) =>
  format(date, formatString, { locale: dateFnsLocale(), ...options });

// date-fns `format()` throws `RangeError: Invalid time value` when handed an
// Invalid Date, so every `new Date(x)` that feeds a formatter must be checked
// first. Note an Invalid Date object is still truthy — `if (!date)` does not
// catch it.
export const isValidDate = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime());

// Report an unparseable input once per site per session so we can find the
// upstream producer (LIF-8) without flooding Bugsnag on every re-render.
const reportedInvalidDateSites = new Set();

export const reportInvalidDate = (site, input) => {
  if (reportedInvalidDateSites.has(site)) return;
  reportedInvalidDateSites.add(site);

  Bugsnag.notify(new Error(`${site} received an unparseable date`), (event) => {
    event.severity = 'warning';
    event.addMetadata('invalid date', {
      site,
      typeof: typeof input,
      value: String(input).slice(0, 200),
    });
  });
};

export const useDateHelper = () => {
  const userTimezoneOffset = new Date().toString().match(/([-\+][0-9]+)\s/)[1];
  const userTimezoneOffsetFormatted = userTimezoneOffset.substring(0, 3) + ':' + userTimezoneOffset.substring(3) ;  

  // TODO: ADD UNIT TEST
  const timeFromNow = (timestamp) => {
    const now = Date.now();
    const currentFrameTime = new Date(timestamp).getTime();
    const timeDelta = currentFrameTime - now;
    const isPast = timeDelta < 0;

    const t = i18n.global.t;

    if (timeDelta === 0) return t('common.time.now');

    const agoSpan = `<span class="time-offset-descriptor">${t('common.time.agoWord')}</span>`;
    const inSpan = `<span class="time-offset-descriptor">${t('common.time.inWord')}</span>`;

    const intervalStartDate = isPast ? new Date(currentFrameTime) : new Date(now);
    const intervalEndDate = isPast ? new Date(now) : new Date(currentFrameTime);

    const { days, hours, minutes, seconds } = intervalToDuration({
      start: intervalStartDate,
      end: intervalEndDate
    })

    let adjustedMinutes = minutes;
    // If timestamp is in the future, add 1 minute to the minutes value to account for the seconds which are not being displayed if seconds are greater than 30 or minutes is 0.
    // If timestamp is 12:00:00 and current time is 11:59:59, the minutes value will be 0 and seconds will be 1. This will display as "0 mins" which is not accurate, so we add 1 minute to the minutes value.
    if (!isPast) {
      adjustedMinutes = minutes === 0 ? 1 : minutes + (seconds > 30 ? 1 : 0);
    }

    const daysString = days >= 1
      ? `${days}<span class="time-offset-label">${t('common.time.dayLabel', days)}</span> `
      : ''

    const hoursString = hours >= 1
      ? `${hours}<span class="time-offset-label">${t('common.time.hourLabel', hours)}</span> `
      : ''

    const minutesString =
      adjustedMinutes === 0 || days > 0 || isNaN(adjustedMinutes)
        ? ''
        : `${adjustedMinutes}<span class="time-offset-label">${t('common.time.minuteLabel', adjustedMinutes)}</span>`

    if ((days === 0 || days === undefined) && (hours === 0 || hours === undefined) && (minutes === 0 || minutes === undefined)) {
      return t('common.time.now');
    }

    const duration = `${daysString}${hoursString}${minutesString}`.trim();

    return isPast
      ? t('common.time.pastPattern', { duration, ago: agoSpan })
      : t('common.time.futurePattern', { duration, in: inSpan });
  }

  const sentTimeLabel = (timestamp) => {
    if (timestamp == null) return '';

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    const t = i18n.global.t;

    if (diffMs < 60 * 60 * 1000) {
      const mins = Math.max(1, diffMin);
      return t('common.time.minsAgo', { n: mins }, mins);
    }
    if (diffMs < 24 * 60 * 60 * 1000) {
      return t('common.time.hoursAgo', { n: diffHr }, diffHr);
    }
    if (diffMs < 7 * 24 * 60 * 60 * 1000) {
      return t('common.time.daysAgo', { n: diffDay }, diffDay);
    }
    if (date.getFullYear() === now.getFullYear()) {
      return formatLocalized(date, 'LLL d');
    }
    return formatLocalized(date, 'LLL d, yyyy');
  }

  const observationTimeLabel = (timestamp) => {
    if (timestamp == null) return '';

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const sameDay = date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth()
      && date.getDate() === now.getDate();
    const sameYear = date.getFullYear() === now.getFullYear();

    const t = i18n.global.t;
    const time = formatLocalized(date, 'p');
    if (sameDay) return t('common.time.todayAt', { time });
    if (sameYear) return t('common.time.dateAt', { date: formatLocalized(date, 'LLL d'), time });
    return t('common.time.dateAt', { date: formatLocalized(date, 'LLL d, yyyy'), time });
  }

  const modelRunTimeFormatted = (options) => {
    const defaultOptions = {
      timestamp: null,
      year: false,
      utcOffset: null,
      timezoneName: null,
      condensed: false
    }

    const { timestamp, year, utcOffset, timezoneName, condensed } = Object.assign({}, defaultOptions, options);

    if (!timestamp) return '';

    const date = new Date(timestamp);
    if (!isValidDate(date)) {
      reportInvalidDate('useDateHelper.modelRunTimeFormatted', timestamp);
      return '';
    }

    const dateFormat = year ? 'ccc, LLL d, y' : condensed ? 'LLL d' : 'ccc, LLL d'

    const dateInTimezone = transpose(new TZDate(date, utcOffset), TZDate);
    if (!isValidDate(dateInTimezone)) {
      reportInvalidDate('useDateHelper.modelRunTimeFormatted.utcOffset', utcOffset);
      return '';
    }

    const formattedDate = formatLocalized(dateInTimezone, dateFormat)

    return `${formattedDate}${timezoneName ? ` (${timezoneName})` : ''}`  
  }

  const dateTimeToISOString = (date, utcOffset = userTimezoneOffsetFormatted) => {
    if (!date) return null;

    return date.replace(' ', 'T') + utcOffset
  }

  const datetimeToISOFormat = (date, utcOffset = userTimezoneOffsetFormatted) => {
    if (!date) return null;

    const isoString = dateTimeToISOString(date, utcOffset);

    return new Date(isoString);
  }

  const adjustDateTimeFromUserTZToTZOffset = (date, utcOffset = userTimezoneOffsetFormatted) => {
    date = new Date(date);

    const userDateLocalTime = date.getTime()
    const userLocalTimezoneOffset = date.getTimezoneOffset() * 60000 // in ms
    const dateUTCOffsetMs = utcOffset * 3600000 // in ms
    const offsetDelta = userLocalTimezoneOffset + dateUTCOffsetMs
    const adjustedDateTimeMs = userDateLocalTime + offsetDelta

    return new Date(adjustedDateTimeMs)    
  }

  const adjustDateTimeFromTZOffsetToUserTZ = (date, utcOffset = userTimezoneOffsetFormatted) => {
    date = new Date(date);

    const userDateLocalTime = date.getTime()
    const userLocalTimezoneOffset = date.getTimezoneOffset() * 60000 // in ms
    const dateUTCOffsetMs = utcOffset * 3600000 // in ms
    const offsetDelta = userLocalTimezoneOffset + dateUTCOffsetMs
    const adjustedDateTimeMs = userDateLocalTime - offsetDelta

    return new Date(adjustedDateTimeMs)    
  }

  const convertISODateToUTC = (isoDateString, returnString = false) => {
    if (isoDateString.includes('Z')) {
      return returnString ? isoDateString : new Date(isoDateString).getTime();
    }
    
    const str = isoDateString;
    const match = str.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2}):([0-9]{2})([+-])([0-9]{2}):([0-9]{2})$/);

    if (match) {
      const [ , year, month, day, hour, min, sec, tzSign, tzHour, tzMin ] = match;
      let y = Number(year);
      let m = Number(month) - 1;
      let d = Number(day);
      let h = Number(hour);
      let mi = Number(min);
      let s = Number(sec);
      let tzh = Number(tzHour);
      let tzm = Number(tzMin);

      // Calculate the timezone offset in minutes
      let offset = (tzh * 60 + tzm) * (tzSign === '+' ? -1 : 1);

      // Build the UTC time in ms
      let utcMS = Date.UTC(y, m, d, h, mi, s);
      utcMS += offset * 60 * 1000;

      return returnString ? new Date(utcMS).toISOString() : utcMS
    }
  }

  const formatOffsetToString = (offset) => {
    const sign = offset >= 0 ? '+' : '-'
    const abs = Math.abs(offset)
    const hours = String(Math.floor(abs)).padStart(2, '0')

    return `${sign}${hours}00`
  }

  return {
    userTimezoneOffset,
    userTimezoneOffsetFormatted,
    adjustDateTimeFromUserTZToTZOffset,
    adjustDateTimeFromTZOffsetToUserTZ,
    convertISODateToUTC,
    datetimeToISOFormat,
    dateTimeToISOString,
    formatOffsetToString,
    isValidDate,
    modelRunTimeFormatted,
    observationTimeLabel,
    sentTimeLabel,
    timeFromNow,
  }
}
